import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useDeleteAccount } from "@/api/auth/mutations";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/auth-context";
import { getApiErrorKey } from "@/utils/errors";

export function DangerZoneSection() {
	const { t } = useTranslation();
	const { logout } = useAuth();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const deleteAccount = useDeleteAccount();
	const [open, setOpen] = useState(false);
	const [deletePassword, setDeletePassword] = useState("");

	useEffect(() => {
		if (open) setDeletePassword("");
	}, [open]);

	function handleDelete() {
		deleteAccount.mutate(
			{ password: deletePassword },
			{
				onSuccess: () => {
					toast.info(t("settings.dangerZone.deletedToast"));
					logout();
					navigate(`/${lang ?? "pl"}/login`);
				},
				onError: (error) => {
					toast.error(t(getApiErrorKey(error)));
				},
			},
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<h2 className="text-sm font-semibold text-destructive">
				{t("settings.dangerZone.title")}
			</h2>
			<div className="rounded-lg border border-destructive/40 divide-y divide-destructive/20">
				<div className="flex items-center justify-between gap-4 px-4 py-4">
					<div>
						<p className="text-sm font-semibold">
							{t("settings.dangerZone.deleteAccount")}
						</p>
						<p className="text-sm text-muted-foreground">
							{t("settings.dangerZone.confirmDescription")}
						</p>
					</div>
					<AlertDialog open={open} onOpenChange={setOpen}>
						<AlertDialogTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								className="shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10"
							>
								{t("settings.dangerZone.deleteAccount")}
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t("settings.dangerZone.confirmTitle")}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t("settings.dangerZone.confirmDescription")}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<div className="grid gap-2 py-2">
								<Label htmlFor="danger-zone-password">
									{t("settings.dangerZone.passwordLabel")}
								</Label>
								<Input
									id="danger-zone-password"
									type="password"
									autoComplete="current-password"
									name="password"
									value={deletePassword}
									onChange={(e) => setDeletePassword(e.target.value)}
								/>
							</div>
							<AlertDialogFooter>
								<AlertDialogCancel>
									{t("settings.dangerZone.cancelButton")}
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={handleDelete}
									disabled={deleteAccount.isPending || !deletePassword}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									{t("settings.dangerZone.confirmButton")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>
		</div>
	);
}
